/* eslint-disable */
// Modules
import Papa from 'papaparse';
import JSZip from 'jszip';

// Config
import config from 'config/config';

// Print single year NCDM table as displayed on page
export const printNcdm = () => {
    window.print();
};

// Function to return string without commas
// (Note: if str is null for some reason, will return empty string (logic in place to prevent attempting a `.replace` on null value)
const sanitizeString = (str) => (str || '') && str.replace(/\,/g, '');

// TODO: We don't have a way to print all years due to paging. Either wait for material-ui to implement or we'll need to
// devise a way to work around paging. Need to reconcile this approach with what is happening on the map/chart pages.

// CSV Exports from XGrid:
export const exportAsCsv = ({
    uiCsv,
    title,
    subtitle,
    columnHeaders,
    yearDetails,
}) => {
    let exportCsv = uiCsv;
    const { data } = Papa.parse(uiCsv);

    const sanitizedTitle = sanitizeString(title);
    const sanitizedSubtitle = sanitizeString(subtitle);
    const sanitizedYearDetails = sanitizeString(yearDetails);

    // Sanitize column headers for unlikely event that comma is present:
    // columnHeaders

    // Replacing missing and suppressed values with corresponding text values
    const formattedData = [...data].map((row) => {
        for (let i = 0; i < row.length; i++) {
            // Note: The Papa.parse() method run on this data converts the values into strings. The missing/suppressed codes are integers in the config file. They are converted into strings for the comparison here:
            if (row[i] === config.missing.toString()) {
                row[i] = config.missingText;
            }
            if (row[i] === config.suppressed.toString()) {
                row[i] = config.suppressedText;
            }
        }
        return row;
    });

    // Column data is stored as first item in array by Papa.parse
    const [columns] = formattedData;

    // Replacing UI column headers with custom export column headers, if any:
    formattedData[0] = columns.map((column) => {
        const { exportHeaderName } = columnHeaders.find(
            (header) => header.headerName === column
        );
        if (exportHeaderName) {
            return sanitizeString(exportHeaderName);
        }
        return sanitizeString(column);
    });

    exportCsv = Papa.unparse(formattedData);

    const BOM = '\uFEFF';
    const finalizedCsv = `${BOM} ${sanitizedTitle}\n\n ${
        sanitizedSubtitle || ''
    } \n\n ${sanitizedYearDetails || ''} \n\n ${exportCsv} \n\n ${
        config.mdhAttribution
    }`;

    const blob = new Blob([finalizedCsv]);

    download('text/csv;charset=utf-8', blob, sanitizedTitle);
};

// Download NCDM data based off of the user's NCDM selections.
// Note this may be a single year or all years for that selection and this only appears on the More Data page.
export const downloadNcdm = (topicNcdms, ncdmSelections, ncdmContent) => {
    const { csvData } = formatDownload(topicNcdms, ncdmSelections, ncdmContent);
    downloadCsv(csvData, ncdmSelections, topicNcdms);
};

// Format the tabular data for csv
export const formatDownload = (topicNcdms, ncdmSelections, ncdmContent) => {
    // ncdmSelections for a single download will reflect what has been selected in the UI
    // For Download All ncdmSelections reflects the current NCDM in the list to download
    const { columns, downloadName } = topicNcdms
        .find((ncdm) => ncdm.indicator === ncdmSelections.indicator)
        .measures.find((item) => item.measure === ncdmSelections.measure);
    const headers = [...columns.map((x) => x.headerName.toUpperCase())];
    const fields = columns.map((x) => x.field);
    const csvFormat = setCsvFormat(formatNcdmContent(ncdmContent));
    const sortedNcdmContent = sortNcdmsStatewideFirst(csvFormat);

    // Convert the json data to csv
    const data = Papa.unparse(sortedNcdmContent, {
        header: false,
        columns: fields,
    });

    // BOM = Byte Order Mark encoding. Set as the first char in the file to force Excel to use UTF-8 to avoid encoding issues.
    const BOM = '\uFEFF';
    const reportDateTime = new Date().toLocaleString('en-US').split(',');

    // Format the csv output
    const csvData =
        `${BOM}SOURCE: Maryland Department of Health Environmental Public Health Tracking \n\n` +
        `DATE:\n${reportDateTime[0].trim()}\n\n` +
        `TIME:\n${reportDateTime[1].trim()}\n\n` +
        `NCDM SELECTION:\n${sanitizeString(
            ncdmSelections.indicator
        )}: ${sanitizeString(ncdmSelections.measure)} for ${
            ncdmSelections.year === '0' ? 'all years' : ncdmSelections.year
        }\n\n` +
        `RESULTS TABLE:\n${headers}\n${data}`;

    return { csvData, ncdmSelections, downloadName };
};

const setCsvFormat = (data) => {
    // Deep clone of the data and remove the id field and any commas within fields
    const clonedData = JSON.parse(JSON.stringify(data)).map((obj) => {
        delete obj.id;
        Object.entries(obj).map(([key, value]) => {
            if (value) {
                obj[key] = value.toString().replace(',', '');
            }
            return obj;
        });
        return obj;
    });

    return clonedData;
};

// Generate the zip file blob
const downloadZip = (files, topic) => {
    const { topicTitle } = topics[topic];
    const zip = new JSZip();
    const zipFolder = zip.folder(`EPHT-${topicTitle} All Data`);

    // Add the files to zip
    files.forEach((file) => {
        const { downloadName } = file;

        zipFolder.file(
            `${file.ncdmSelections.indicator}-${downloadName}.csv`,
            file.csvData
        );
    });

    zip.generateAsync({ type: 'blob' }).then((blob) => {
        download('application/zip', blob, topicTitle);
    });
};

// Generate the csv file blob for single file download
const downloadCsv = (data, ncdmSelections, topicNcdms) => {
    const { downloadName } = topicNcdms
        .find((ncdm) => ncdm.indicator === ncdmSelections.indicator)
        .measures.find((item) => item.measure === ncdmSelections.measure);
    const name = `${ncdmSelections.indicator}-${downloadName}`;

    const blob = new Blob([data]);
    download('text/csv;charset=utf-8', blob, name);
};

// Initiate download of file
const download = (type, blob, name) => {
    const a = window.document.createElement('a');
    a.href = window.URL.createObjectURL(blob, {
        type,
    });
    a.download =
        type === 'application/zip' ? `EPHT ${name}.zip` : `EPHT ${name}.csv`;

    // Windows has filepath length limits which we may likely exceed if the filename exceeds 150 characters
    if (a.download.length > 150) {
        console.warn('Filename length exceeds recommended length');
    }

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

// Sort NCDMs alphabetically by jurisdiction with statewide first and by year ascending within a jurisdiction.
export const sortNcdmsStatewideFirst = (ncdmContent) => {
    // Need to copy the array before sorting since arrays are frozen in strict mode
    const sorted = [...ncdmContent].sort((a, b) => {
        if (a.jurisdiction !== 'Statewide' && b.jurisdiction === 'Statewide') {
            return 1;
        }
        if (a.jurisdiction === 'Statewide' && b.jurisdiction !== 'Statewide') {
            return -1;
        }
        if (a.jurisdiction === b.jurisdiction) {
            return a.yearsend - b.yearsend;
        }

        return a.jurisdiction > b.jurisdiction ? 1 : -1;
    });
    return sorted;
};

// Format NCDM data to replace -999 and -777 with missing and suppressed text and format numbers
export const formatNcdmContent = (ncdmContent) => {
    const formattedContent = ncdmContent.map((entry) => {
        Object.entries(entry).map(([key, value]) => {
            if (value === config.missing) {
                entry[key] = config.missingText;
            } else if (value === config.suppressed) {
                entry[key] = config.suppressedText;
            } else if (key !== 'yearsend' && typeof value === 'number') {
                entry[key] = value.toLocaleString();
            }
            return entry;
        });
        return entry;
    });
    return formattedContent;
};
